'use client'

//___IMPORTS
import Link from 'next/link'
import React, { useEffect, useMemo, useState } from 'react'

//__TYPES
import { Branch, IBranch } from '../types/schema'

//__STYLES
import '@/styles/components/_cityContainer.scss'

//__INTERFACES
interface CityContainerProps {
  city: IBranch
}

export default function CityContainer({ city }: CityContainerProps) {
  //__PROPS
  if (!city || !city.code) return null

  // State to track client-side rendering
  const [isClient, setIsClient] = useState(false)

  // Effect to set isClient after component mounts
  useEffect(() => {
    setIsClient(true)
  }, [])

  // Use useMemo to optimize expensive calculations
  const { cityUrl, sortedBranches, branchUrls } = useMemo(() => {
    // URL normalization with consistent behavior
    const normalizeUrl = (url: string) => {
      // During initial render, use the original URL to match the server
      if (!isClient) return url

      // Only perform client-side URL modification after hydration
      return url.replace(/https?:\/\/[^\/]+/, window.location.origin)
    }

    // Get city URL
    const getCityUrl = (branches: IBranch['branches']) => {
      if (!branches?.[0]?.url) return '#'
      const baseUrl = branches[0].url.split('/').slice(0, -2).join('/')
      return normalizeUrl(baseUrl)
    }

    // Pre-compute all branch URLs
    const branchUrls = city.branches.reduce(
      (acc, branch) => {
        acc[Number(branch.id)] = normalizeUrl(branch.url)
        return acc
      },
      {} as Record<number, string>
    )

    // Pre-sort branches once
    const sortedBranches = [...city.branches].sort(
      (a: Branch, b: Branch) => Number(b.id) - Number(a.id)
    )

    return {
      cityUrl: getCityUrl(city.branches),
      sortedBranches,
      branchUrls
    }
  }, [city, isClient]) // Add isClient as a dependency

  //__RENDER
  return (
    <article className='ui--city'>
      <h2 className='ui--city-header'>
        <Link href={cityUrl} prefetch={false}>
          {city.name}
        </Link>
      </h2>
      <ul className='ui--city-list'>
        {sortedBranches.map((branch) => (
          <li key={branch.id} className='ui--city-list-item'>
            <Link href={branchUrls[Number(branch.id)]} prefetch={false} title={branch.nameandarea}>
              {branch.nameandarea}
            </Link>
          </li>
        ))}
      </ul>
    </article>
  )
}
